// Shield-Wall — two verification buttons that appear on every active
// hazard card. Voting is auth-only; guests get the standard sign-in
// nudge. On the third "Path Clear" vote the backend auto-archives the
// hazard, so we refetch the parent Known Issues query to hide it. On
// the third "Still Here" vote the reporter earns the Watchman badge —
// we don't celebrate it in the voter's UI (they didn't earn it), we
// just render a subtle "Verified by the pack" tag on the card.
import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { guestNudge } from "../lib/achievementUX";
import { track } from "../lib/telemetry";

const CHIP_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-60";

export default function HazardVoteButtons({ trailId, report }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(null); // "still_here" | "path_clear" | null
  const [state, setState] = useState({
    still_here_count: report.still_here_count || 0,
    path_clear_count: report.path_clear_count || 0,
    viewer_still_here: !!report.viewer_still_here,
    viewer_path_clear: !!report.viewer_path_clear,
  });
  // Re-sync when the parent refetches (e.g. after another walker's vote
  // tips the report over the Rule-of-3 threshold and the Known Issues
  // list is invalidated). Without this, our local state can lag the DB.
  useEffect(() => {
    setState({
      still_here_count: report.still_here_count || 0,
      path_clear_count: report.path_clear_count || 0,
      viewer_still_here: !!report.viewer_still_here,
      viewer_path_clear: !!report.viewer_path_clear,
    });
  }, [
    report.report_id,
    report.still_here_count,
    report.path_clear_count,
    report.viewer_still_here,
    report.viewer_path_clear,
  ]);
  const threshold = report.shield_wall_threshold || 3;
  const stillHereVerified = state.still_here_count >= threshold;

  const cast = async (vote_type, ev) => {
    if (guestNudge(user, ev)) return;
    if (busy) return;
    setBusy(vote_type);
    try {
      const res = await api.post(`/trails/${trailId}/reports/${report.report_id}/vote`, {
        vote_type,
      });
      setState({
        still_here_count: res.data.still_here_count,
        path_clear_count: res.data.path_clear_count,
        viewer_still_here: !!res.data.viewer_still_here,
        viewer_path_clear: !!res.data.viewer_path_clear,
      });
      track("hazard_vote", {
        trail_id: trailId,
        report_id: report.report_id,
        vote_type,
      });
      if (res.data.community_resolved) {
        toast.success("Hazard cleared — thanks for keeping the pack safe.");
        // Refetch so the archived report disappears from the list.
        qc.invalidateQueries({ queryKey: ["trail-reports", trailId] });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't record your vote. Try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-2"
      data-testid={`hazard-votes-${report.report_id}`}
    >
      <button
        type="button"
        onClick={(ev) => cast("still_here", ev)}
        disabled={busy !== null}
        aria-pressed={state.viewer_still_here}
        data-testid={`vote-still-here-${report.report_id}`}
        data-voted={state.viewer_still_here ? "true" : "false"}
        className={
          CHIP_BASE + " " +
          (state.viewer_still_here
            ? "bg-amber-200 border-amber-500 text-amber-900"
            : "bg-white/70 border-amber-300 text-amber-800 hover:border-amber-500")
        }
        title="Confirm this hazard is still here"
      >
        {busy === "still_here" ? (
          <Loader2 size={11} className="animate-spin" />
        ) : (
          <ShieldAlert size={11} />
        )}
        <span>Still here</span>
        <span className="tabular-nums opacity-80" data-testid={`vote-still-here-count-${report.report_id}`}>
          {state.still_here_count}
        </span>
      </button>
      <button
        type="button"
        onClick={(ev) => cast("path_clear", ev)}
        disabled={busy !== null}
        aria-pressed={state.viewer_path_clear}
        data-testid={`vote-path-clear-${report.report_id}`}
        data-voted={state.viewer_path_clear ? "true" : "false"}
        className={
          CHIP_BASE + " " +
          (state.viewer_path_clear
            ? "bg-emerald-200 border-emerald-500 text-emerald-900"
            : "bg-white/70 border-emerald-300 text-emerald-800 hover:border-emerald-500")
        }
        title="Confirm the path is now clear"
      >
        {busy === "path_clear" ? (
          <Loader2 size={11} className="animate-spin" />
        ) : (
          <CheckCircle2 size={11} />
        )}
        <span>Path clear</span>
        <span className="tabular-nums opacity-80" data-testid={`vote-path-clear-count-${report.report_id}`}>
          {state.path_clear_count}
        </span>
        <span className="opacity-60">/ {threshold}</span>
      </button>
      {stillHereVerified && (
        <span
          data-testid={`hazard-verified-${report.report_id}`}
          className="inline-flex items-center gap-1 rounded-full bg-amber-900/10 border border-amber-900/25 text-amber-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
        >
          Verified by the pack
        </span>
      )}
    </div>
  );
}
